/**
 * "Configure" affordance rendered inside every `withComponentRules`
 * wrapper as a flex child of the HOC's absolutely-positioned overlay
 * container (shared with `ToggleRulesButton`). Visible while config
 * mode is on; otherwise renders `null`. Links to the registered
 * component's admin config page via React Router so native
 * open-in-new-tab / middle-click behaviour is preserved.
 *
 * Access to the linked `/admin/*` page is enforced by `AdminAuthGuard`
 * on the admin routes, so non-admin users who click through land on the
 * admin login page — no privilege leak from this surface.
 */

import type { MouseEvent } from 'react';
import { Link } from 'react-router-dom';
import { Settings } from 'lucide-react';

import { buildConfigPath } from './configClickHandlerUtils';
import { useConfigMode } from './configModeStore';

interface ConfigureButtonProps {
  /** The registered component id this button configures. */
  componentId: string;
}

export function ConfigureButton({ componentId }: ConfigureButtonProps) {
  const { enabled } = useConfigMode();

  if (!enabled) {
    return null;
  }

  // Stop propagation so clicking the button does not bubble up to an
  // ancestor's own `onClick` (e.g., a row that navigates on click). We
  // deliberately do NOT call `preventDefault` — the Link must still
  // perform its normal router navigation.
  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    event.stopPropagation();
  };

  const label = `Configure ${componentId}`;

  return (
    <Link
      to={buildConfigPath(componentId)}
      onClick={handleClick}
      aria-label={label}
      title={label}
      className="inline-flex items-center gap-1 rounded-full border border-neutral-300 bg-neutral-50 px-2 py-1 text-xs text-neutral-700 shadow-sm hover:bg-neutral-100"
    >
      <Settings className="w-3 h-3" aria-hidden="true" />
      <span>Configure</span>
    </Link>
  );
}
